import { serverBaseUrl } from "../config/envs";

const MAX_PDF_BYTES = 10 * 1024 * 1024;
const sha256 = (value) => typeof value === "string" && /^[a-f0-9]{64}$/.test(value);
const uuid = (value) => typeof value === "string" && /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(value);
const calendarDate = (value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)
  && !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;

async function request(secret, path, payload, valid) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000);
  let response;
  let body;
  try {
    response = await fetch(`${serverBaseUrl}/${path}`, {
      method: "POST", headers: { "sign-secret-id": secret, "Content-Type": "application/json" },
      credentials: "omit", cache: "no-store", referrerPolicy: "no-referrer", signal: controller.signal,
      body: JSON.stringify(payload),
    });
    try { body = await response.json(); } catch { /* Proxies can return non-JSON failures. */ }
  } catch {
    const failure = new Error("Could not confirm this request. Check your connection and try again; a retry will not sign twice.");
    failure.status = 503;
    throw failure;
  } finally {
    clearTimeout(timeout);
  }
  if (response.ok && body?.success === true && valid(body.data)) return body.data;
  const paused = response.status === 404 && body?.messageCode === 1156;
  const failure = new Error(paused ? "This document is not available right now. Please try again later."
    : response.status === 400 ? "Check your fields and signature, then try again."
    : [401, 403, 404].includes(response.status) ? "This signing link is unavailable. Ask your administrator for a valid link."
      : response.status === 409 ? "This document changed, was voided, or is not ready for your signature. Reload before continuing."
        : response.ok ? "The document returned an unexpected response. Reload before continuing."
          : "Could not confirm this request. Please try again; a retry will not sign twice.");
  failure.status = response.status;
  if (paused) failure.code = 1156;
  throw failure;
}

export function cpPreviewBytes(base64) {
  if (typeof base64 !== "string" || !base64 || base64.length > Math.ceil(MAX_PDF_BYTES / 3) * 4
    || base64.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/.test(base64)) return null;
  try {
    const decoded = atob(base64);
    if (decoded.length > MAX_PDF_BYTES || !decoded.startsWith("%PDF-")) return null;
    return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
  } catch { return null; }
}

export async function previewCpDocument(secret, payload) {
  const data = await request(secret, "cp_document_sign_preview", payload, (value) =>
    value && cpPreviewBytes(value.pdf_base64) !== null && sha256(value.pdf_sha256) && calendarDate(value.signing_date));
  const bytes = cpPreviewBytes(data.pdf_base64);
  let digest;
  try {
    digest = Array.from(new Uint8Array(await window.crypto.subtle.digest("SHA-256", bytes)), (byte) => byte.toString(16).padStart(2, "0")).join("");
  } catch {
    const failure = new Error("Your browser could not verify this document. Use an up-to-date browser on a secure connection and reload.");
    failure.status = 200;
    throw failure;
  }
  if (digest !== data.pdf_sha256) {
    const failure = new Error("The reviewed document could not be verified. Reload before continuing.");
    failure.status = 200;
    throw failure;
  }
  return { ...data, pdf_bytes: bytes };
}

export function signCpDocument(secret, payload) {
  return request(secret, "cp_document_sign", payload, (value) => value && uuid(value.receipt_id) && sha256(value.pdf_sha256)
    && value.pdf_sha256 === payload.reviewed_sha256 && typeof value.accepted_at === "string"
    && /^\d{4}-\d{2}-\d{2}T/.test(value.accepted_at) && Number.isFinite(Date.parse(value.accepted_at))
    && ["physician", "operator"].includes(value.role_key) && ["pending", "signed", "void"].includes(value.document_status));
}
